import { useEffect, useRef, useState } from "react";
import { Award, Printer, Upload, Trash2, Music } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const AWARD_PRESETS = [
  "Music Theory Grade 1", "Music Theory Grade 2", "Music Theory Grade 3", "Music Theory Grade 4", "Music Theory Grade 5",
  "Sight-Singing", "Aural Skills", "Rhythm Lab", "Score Reading", "Choir Skills", "Psalm Singing & Anglican Chant",
];
const ACHIEVEMENTS = ["Pass", "Merit", "Distinction"];
const LS = { logo: "cq_cert_logo", choir: "cq_cert_choir", director: "cq_cert_director" };

function Seal() {
  return (
    <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-full border-4 border-[#C69B3D] bg-[#C69B3D] flex flex-col items-center justify-center text-[#0B192C] shadow-inner shrink-0" aria-hidden>
      <Music className="w-5 h-5 sm:w-6 sm:h-6" />
      <span className="text-[6px] sm:text-[7px] font-bold uppercase tracking-widest mt-0.5">ChoirQuest</span>
    </div>
  );
}

function CertificateSheet({ logo, choirName, choristerName, award, achievement, dateStr, director }) {
  return (
    <div id="certificate-sheet" data-testid="certificate-sheet"
      className="bg-[#FAF7EE] text-[#0B192C] w-full max-w-[1050px] mx-auto aspect-[297/210] p-[3.5%] shadow-2xl border border-[#E5DEC9]">
      <div className="h-full w-full border-[3px] border-[#C69B3D] p-[5px]">
        <div className="h-full w-full border border-[#0B192C]/60 px-[4%] py-[3%] flex flex-col items-center text-center relative">
          {logo && <img src={logo} alt="Choir logo" data-testid="certificate-logo" className="max-h-[13%] max-w-[30%] object-contain mb-1" />}
          <p className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.35em] text-[#566270]" data-testid="certificate-choir-name">{choirName || "Your Choir"}</p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-[2.6rem] font-bold mt-[2%] text-[#0B192C]">Certificate of Musical Achievement</h1>
          <div className="flex items-center gap-3 mt-[1.5%] w-2/3" aria-hidden>
            <span className="flex-1 h-px bg-[#C69B3D]" />
            <span className="w-2 h-2 rotate-45 bg-[#C69B3D]" />
            <span className="flex-1 h-px bg-[#C69B3D]" />
          </div>
          <p className="mt-[3%] text-sm text-[#566270] italic">This is to certify that</p>
          <p className="font-display italic font-bold text-3xl sm:text-4xl lg:text-[2.8rem] leading-tight mt-[1%] px-6 border-b-2 border-[#C69B3D] pb-2 min-w-[50%]" data-testid="certificate-chorister-name">
            {choristerName || "Chorister's Name"}
          </p>
          <p className="mt-[2.5%] text-sm text-[#566270] italic">has been awarded</p>
          <p className="font-display text-xl sm:text-2xl font-bold mt-[1%] text-[#7A1C2E]" data-testid="certificate-award">
            {award || "Music Theory Grade 1"}{achievement ? ` — with ${achievement}` : ""}
          </p>
          <div className="mt-auto w-full flex items-end justify-between gap-2 pt-[2%]">
            <div className="text-left min-w-0">
              <p className="text-xs sm:text-sm font-semibold border-b border-[#0B192C]/50 pb-1 min-w-[80px] sm:min-w-[140px] truncate" data-testid="certificate-date">{dateStr}</p>
              <p className="text-[9px] sm:text-[10px] uppercase tracking-widest text-[#566270] mt-1">Date</p>
            </div>
            <Seal />
            <div className="text-right min-w-0">
              <p className="font-display italic text-sm sm:text-lg border-b border-[#0B192C]/50 pb-1 min-w-[90px] sm:min-w-[160px] truncate" data-testid="certificate-director">{director || "\u00A0"}</p>
              <p className="text-[9px] sm:text-[10px] uppercase tracking-widest text-[#566270] mt-1">Director of Music</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CertificatesPage() {
  const { session } = useAuth();
  const [logo, setLogo] = useState(() => localStorage.getItem(LS.logo) || "");
  const [choirName, setChoirName] = useState(() => localStorage.getItem(LS.choir) || session?.profile?.institution_name || "");
  const [choristerName, setChoristerName] = useState("");
  const [awardChoice, setAwardChoice] = useState(AWARD_PRESETS[0]);
  const [customAward, setCustomAward] = useState("");
  const [achievement, setAchievement] = useState("Distinction");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [director, setDirector] = useState(() => localStorage.getItem(LS.director) || session?.profile?.name || "");
  const fileRef = useRef(null);

  useEffect(() => {
    const cleanup = () => document.body.classList.remove("cert-printing");
    window.addEventListener("afterprint", cleanup);
    return () => window.removeEventListener("afterprint", cleanup);
  }, []);

  const onLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f || !f.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = () => { setLogo(reader.result); localStorage.setItem(LS.logo, reader.result); };
    reader.readAsDataURL(f);
  };
  const removeLogo = () => { setLogo(""); localStorage.removeItem(LS.logo); if (fileRef.current) fileRef.current.value = ""; };
  const saveChoir = (v) => { setChoirName(v); localStorage.setItem(LS.choir, v); };
  const saveDirector = (v) => { setDirector(v); localStorage.setItem(LS.director, v); };

  const award = awardChoice === "__custom" ? customAward : awardChoice;
  const dateStr = date ? new Date(`${date}T12:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "";

  const doPrint = () => {
    document.body.classList.add("cert-printing");
    window.print();
  };

  const inputCls = "w-full rounded-xl border border-[#E5DEC9] bg-white px-4 py-3 text-sm text-[#1C242B] min-h-[48px]";
  const labelCls = "block text-xs font-bold uppercase tracking-widest text-[#566270] mb-1.5";

  return (
    <div className="space-y-6" data-testid="certificates-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0B192C]">Certificates</h1>
          <p className="text-sm text-[#566270] mt-1">Create a printable certificate. Names are typed by hand — no chorister data leaves this page.</p>
        </div>
        <button onClick={doPrint} data-testid="cert-print-btn"
          className="inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-[#0B192C] text-[#FAF7EE] font-bold text-sm hover:bg-[#12253E] transition-colors min-h-[48px]">
          <Printer className="w-4 h-4" /> Print / Save as PDF
        </button>
      </div>

      <div className="grid lg:grid-cols-[360px_1fr] gap-6 items-start">
        <section className="rounded-2xl bg-white border border-[#E5DEC9] p-6 space-y-5" data-testid="cert-form">
          <div>
            <label className={labelCls}>Choir logo (optional)</label>
            <div className="flex items-center gap-2">
              <button onClick={() => fileRef.current?.click()} data-testid="cert-logo-upload-btn"
                className="inline-flex items-center gap-2 px-4 py-3 rounded-xl border border-[#C69B3D] text-[#0B192C] font-semibold text-sm hover:bg-[#F6EED8] transition-colors min-h-[48px]">
                <Upload className="w-4 h-4" /> {logo ? "Replace logo" : "Upload logo"}
              </button>
              {logo && (
                <button onClick={removeLogo} data-testid="cert-remove-logo-btn" aria-label="Remove logo"
                  className="inline-flex items-center justify-center w-12 h-12 rounded-xl border border-[#E5DEC9] text-[#7A1C2E] hover:bg-[#F6EED8] transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" onChange={onLogo} className="hidden" data-testid="cert-logo-input" />
          </div>
          <div>
            <label className={labelCls} htmlFor="cert-choir">Choir / institution name</label>
            <input id="cert-choir" value={choirName} onChange={(e) => saveChoir(e.target.value)} className={inputCls} data-testid="cert-choir-name-input" />
          </div>
          <div>
            <label className={labelCls} htmlFor="cert-chorister">Chorister name</label>
            <input id="cert-chorister" value={choristerName} onChange={(e) => setChoristerName(e.target.value)} placeholder="e.g. Emily Smith" className={inputCls} data-testid="cert-chorister-name-input" />
          </div>
          <div>
            <label className={labelCls} htmlFor="cert-award">Award</label>
            <select id="cert-award" value={awardChoice} onChange={(e) => setAwardChoice(e.target.value)} className={inputCls} data-testid="cert-award-select">
              {AWARD_PRESETS.map((a) => <option key={a} value={a}>{a}</option>)}
              <option value="__custom">Custom award…</option>
            </select>
            {awardChoice === "__custom" && (
              <input value={customAward} onChange={(e) => setCustomAward(e.target.value)} placeholder="e.g. Head Chorister" className={`${inputCls} mt-2`} data-testid="cert-award-custom-input" />
            )}
          </div>
          <div>
            <label className={labelCls} htmlFor="cert-achievement">Achievement</label>
            <select id="cert-achievement" value={achievement} onChange={(e) => setAchievement(e.target.value)} className={inputCls} data-testid="cert-achievement-select">
              {ACHIEVEMENTS.map((a) => <option key={a} value={a}>with {a}</option>)}
              <option value="">No achievement line</option>
            </select>
          </div>
          <div>
            <label className={labelCls} htmlFor="cert-date">Date</label>
            <input id="cert-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} data-testid="cert-date-input" />
          </div>
          <div>
            <label className={labelCls} htmlFor="cert-director">Signature name</label>
            <input id="cert-director" value={director} onChange={(e) => saveDirector(e.target.value)} placeholder="Director of Music" className={inputCls} data-testid="cert-director-input" />
          </div>
        </section>

        <div className="space-y-3 min-w-0">
          <p className="text-xs font-bold uppercase tracking-widest text-[#566270] flex items-center gap-2"><Award className="w-4 h-4 text-[#C69B3D]" /> Preview (prints on A4 landscape)</p>
          <CertificateSheet logo={logo} choirName={choirName} choristerName={choristerName} award={award} achievement={achievement} dateStr={dateStr} director={director} />
        </div>
      </div>
    </div>
  );
}
